import type { JSX } from 'react';

import { Badge } from '@/components/ui/badge.js';

/**
 * A MARK on a row: a state the row is in, as opposed to something the row lets you do.
 *
 * It lives in the shared kit because a list built of picker rows and a list built of plain rows must mark
 * a row the same way — the alternative, a mark passed in as a per-row "action", is what kept "already in
 * the knowledge base" on one SharePoint surface out of three: a surface whose rows are pick toggles has no
 * place to hang an action, so it silently had no mark either.
 *
 * A mark is three things at once, and each of them is for a different reader:
 * - the WORDS, as a badge, for anyone looking at the row;
 * - the same words inside the row's accessible NAME ({@link nameWithMark}), for a screen reader, so a
 *   marked row and a plain one are not announced identically;
 * - a COLOUR on the row's leading edge ({@link rowMarkClass}), so the row stands out in a long list.
 *
 * The colour is the secondary signal, never the only one: it is redundant with the badge and with the
 * accessible name, which is what makes the mark work for a colour-blind reader and for a screen reader.
 */

/**
 * The mark itself. A neutral badge on purpose — it inherits the theme's own surface, so it keeps its text
 * contrast in the light and the dark theme alike, which a fixed brand colour at this size would not.
 */
export function RowMark({
  label,
  title,
}: {
  label: string;
  /** What the mark's two or three words mean, in full, for a reader who hovers them. */
  title?: string;
}): JSX.Element {
  return (
    <Badge variant="secondary" className="shrink-0 text-[10px]" title={title}>
      {label}
    </Badge>
  );
}

/**
 * What a marked row wears, as classes for the row itself.
 *
 * The colour is on the leading EDGE rather than in the row's fill, and that split is the whole point: on a
 * surface that picks files the fill already means "picked", and a file can perfectly well be picked AND
 * marked at the same moment. Two states on two different channels both stay readable; two states competing
 * for the background would leave the user seeing whichever one won.
 *
 * The edge is also reserved on an UNMARKED row, transparent. Otherwise marking a row would push its
 * content two pixels sideways and the list would visibly jog as the shelf answer arrives.
 *
 * `primary` is the one accent this theme carries that is legible against both the light and the dark
 * surface. Nothing sits on top of it — it is a 2px rail beside the text, not a wash behind it — so the row
 * reads exactly as well marked as unmarked.
 */
export function rowMarkClass(marked: boolean): string {
  return marked
    ? 'border-l-2 border-l-primary'
    : 'border-l-2 border-l-transparent';
}

/**
 * The row's accessible name once its mark is part of it. A mark that only reaches the eye leaves a screen
 * reader with two rows it cannot tell apart, which is the same defect as two search hits sharing a name.
 */
export function nameWithMark(name: string, mark: string | undefined): string {
  return mark === undefined ? name : `${name}, ${mark}`;
}
